/**
 * Tool Stream Utilities
 *
 * Provides helpers for parsing UI tool stream arguments progressively.
 * Used to convert tool call arguments into UIBlock data for rendering.
 */

import type { UIBlock, ContainerBlock } from '@/types/ui-blocks';
import { normalizeBlock } from '@/lib/block-normalizer';

// ---------------------------------------------------------------------------
// Block type classification
// ---------------------------------------------------------------------------

const INTERACTIVE_BLOCK_TYPES = new Set([
  'select', 'confirmation', 'radio', 'editable_list', 'selectable_table',
  'text_input', 'schedule_input', 'channel_input', 'routine_summary'
]);

export type ExperienceType = 'insight_preview' | 'report_preview' | 'blog_refresh_queue' | 'content_outline_queue' | 'content_editor' | 'keyword_review' | 'experiments';

const EXPERIENCE_TYPES = new Set<ExperienceType>([
  'insight_preview', 'report_preview', 'blog_refresh_queue',
  'content_outline_queue', 'content_editor', 'keyword_review', 'experiments'
]);

/**
 * Map a UI tool name to a UIBlock type.
 * Returns null if the tool name doesn't correspond to a UI tool.
 *
 * @example
 * getBlockTypeFromToolName('ui_text')       // 'text'
 * getBlockTypeFromToolName('ui_table')      // 'table'
 * getBlockTypeFromToolName('ui_line_chart') // 'line_chart'
 * getBlockTypeFromToolName('web_search')    // null
 */
export function getBlockTypeFromToolName(toolName: string): string | null {
  if (!toolName.startsWith('ui_')) return null;
  return toolName.slice(3);
}

/**
 * Whether a tool call writes an artifact page, and which kind of write.
 *
 * Not UI tools: the server forwards their stream only so the chat can show a
 * pending artifact card while the page is being written.
 *
 * @example
 * artifactDraftModeOf('create_artifact') // 'create'
 * artifactDraftModeOf('ui_table')        // null
 */
export function artifactDraftModeOf(toolName: string): 'create' | 'update' | null {
  if (toolName === 'create_artifact') return 'create';
  if (toolName === 'update_artifact') return 'update';
  return null;
}

/**
 * Read one string field out of a tool call's arguments while they are still
 * streaming, as soon as that field's value has closed. Returns null until then,
 * and for a field that is absent, null, or empty.
 *
 * @example
 * extractStreamingStringField('{"title": "Weekly re', 'title')   // null
 * extractStreamingStringField('{"title": "Weekly review", "', 'title') // 'Weekly review'
 */
export function extractStreamingStringField(partialArgs: string, field: string): string | null {
  const match = new RegExp(`"${field}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`).exec(partialArgs);
  if (!match) return null;
  try {
    const value = JSON.parse(`"${match[1]}"`) as string;
    return value.trim() ? value.trim() : null;
  } catch {
    return null;
  }
}

/** Returns true if the block type requires user interaction (select, confirmation, etc.) */
export function isInteractiveBlockType(blockType: string): boolean {
  return INTERACTIVE_BLOCK_TYPES.has(blockType);
}

/** Returns true if the block type is an experience/behaviour tool (insight_preview, report_preview, etc.) */
export function isExperienceType(blockType: string): blockType is ExperienceType {
  return EXPERIENCE_TYPES.has(blockType as ExperienceType);
}

// ---------------------------------------------------------------------------
// Block builders
// ---------------------------------------------------------------------------

/**
 * Builds a text block with an auto-triggered agent_experience behaviour.
 * Used by both the streaming handler and history transform when an experience
 * tool (insight_preview, report_preview, etc.) is encountered.
 *
 * The built block is run through `normalizeBlock` so Anthropic-skeleton
 * stringified payloads inside `action.data` (notably
 * `insight_preview.blocks: string[]`, where each entry is JSON-encoded) are
 * parsed into real block objects before the slot consumes them. Without
 * this, `BlocksPreviewSlot` dispatches the JSON strings as blocks and the
 * chat message fails with the renderer's error boundary.
 *
 * When the tool call's own id is known, the behaviour and action ids are
 * derived from it, so the same tool call gets the same ids whether it is
 * built while streaming or rebuilt from history on reload. Slots key their
 * persisted state (e.g. `entries_stored`) by the action id; a fresh random
 * id on every reload meant that state was never found again, and queue
 * slots re-submitted their entries each time the chat was reopened.
 */
export function buildExperienceBlock(
  experienceType: ExperienceType,
  args: Record<string, unknown>,
  toolCallId?: string,
  fromHistory = false
): UIBlock {
  const block: UIBlock = {
    type: 'text',
    version: '1.0',
    content: (args.title as string) || '',
    behaviours: [{
      id: toolCallId ? `${toolCallId}:behaviour` : crypto.randomUUID(),
      trigger: 'auto' as const,
      action: {
        id: toolCallId ? `${toolCallId}:action` : crypto.randomUUID(),
        type: 'agent_experience' as const,
        agent_short_name: (args.agent_short_name as string | undefined) ?? '',
        experience: experienceType,
        data: args,
        ...(fromHistory && { from_history: true })
      }
    }]
  };
  return normalizeBlock(block) as UIBlock;
}

/**
 * Strips group_id and group_config from tool args, returning clean args
 * and optional group metadata. Used by both streaming and history paths.
 */
export function extractGroupMeta(args: Record<string, unknown>): {
  cleanArgs: Record<string, unknown>;
  meta: ToolStreamBlockMeta | null;
} {
  if (!args.group_id) return { cleanArgs: args, meta: null };
  const { group_id, group_config, ...cleanArgs } = args;
  return {
    cleanArgs,
    meta: {
      __group_id: group_id as string,
      __group_config: group_config as GroupConfig | undefined,
    }
  };
}

// ---------------------------------------------------------------------------
// Group assembly
// ---------------------------------------------------------------------------

export interface GroupConfig {
  layout?: 'masonry' | 'grid' | 'flex';
  columns?: number;
  gap?: number;
  heading?: string;
  description?: string;
  border?: boolean;
  borderColor?: 'red' | 'green' | 'yellow' | 'gray' | 'blue' | 'purple';
}

/** Metadata attached to blocks during tool streaming for group tracking. */
export interface ToolStreamBlockMeta {
  __group_id?: string;
  __group_config?: GroupConfig;
}

/**
 * Assembles flat blocks into grouped containers.
 *
 * Blocks with the same `__group_id` in their metadata are wrapped in a
 * ContainerBlock using the group_config from the metadata map.
 * Blocks without a group_id pass through unchanged.
 * Order is preserved: the container appears at the position of the first block in the group.
 */
export function assembleGroupedBlocks(
  blocks: UIBlock[],
  blockMeta: Map<number, ToolStreamBlockMeta>
): (UIBlock | ContainerBlock)[] {
  const result: (UIBlock | ContainerBlock)[] = [];
  const groupBlocks = new Map<string, UIBlock[]>();
  const groupConfigs = new Map<string, GroupConfig>();

  for (let i = 0; i < blocks.length; i++) {
    const meta = blockMeta.get(i);
    if (meta?.__group_id) {
      const gid = meta.__group_id;
      if (!groupBlocks.has(gid)) {
        groupBlocks.set(gid, []);
        if (meta.__group_config) {
          groupConfigs.set(gid, meta.__group_config);
        }
      }
      groupBlocks.get(gid)!.push(blocks[i]);
    }
  }

  // Build result: iterate blocks in order, inserting containers at first-block position
  const emittedGroups = new Set<string>();
  for (let i = 0; i < blocks.length; i++) {
    const meta = blockMeta.get(i);
    if (meta?.__group_id) {
      const gid = meta.__group_id;
      if (!emittedGroups.has(gid)) {
        emittedGroups.add(gid);
        const cfg = groupConfigs.get(gid) ?? {};
        const container: ContainerBlock = {
          type: 'container',
          version: '1.0',
          layout: cfg.layout,
          columns: cfg.columns as ContainerBlock['columns'],
          gap: cfg.gap,
          heading: cfg.heading,
          description: cfg.description,
          border: cfg.border,
          borderColor: cfg.borderColor,
          blocks: groupBlocks.get(gid)!
        };
        result.push(container);
      }
      // skip individual grouped blocks — they're already in the container
    } else {
      result.push(blocks[i]);
    }
  }

  return result;
}

/**
 * Extract partial text content from streaming tool arguments JSON.
 *
 * Used for the `ui_text` tool to progressively stream the "content" field.
 * The arguments JSON has the shape: {"content": "some markdown text"}.
 * This function extracts whatever portion of the content string has arrived,
 * correctly handling JSON escape sequences including unicode (\uXXXX).
 *
 * Returns null if the "content" field hasn't started streaming yet.
 */
export function extractTextFromToolArgs(argsJson: string): string | null {
  const match = argsJson.match(/"content"\s*:\s*"/);
  if (!match || match.index === undefined) return null;

  const start = match.index + match[0].length;
  let content = '';
  let escape = false;

  for (let i = start; i < argsJson.length; i++) {
    const char = argsJson[i];

    if (escape) {
      switch (char) {
        case 'n': content += '\n'; break;
        case 'r': content += '\r'; break;
        case 't': content += '\t'; break;
        case 'b': content += '\b'; break;
        case 'f': content += '\f'; break;
        case '"': content += '"'; break;
        case '\\': content += '\\'; break;
        case '/': content += '/'; break;
        case 'u': {
          const hex = argsJson.slice(i + 1, i + 5);
          if (hex.length === 4 && /^[0-9a-fA-F]{4}$/.test(hex)) {
            content += String.fromCharCode(parseInt(hex, 16));
            i += 4;
          } else {
            return content; // incomplete unicode escape
          }
          break;
        }
        default: content += char;
      }
      escape = false;
      continue;
    }

    if (char === '\\') {
      escape = true;
      continue;
    }

    if (char === '"') {
      return content; // end of content string
    }

    content += char;
  }

  // Content string not yet closed, return what we have
  return content;
}
